// ── Enums ──────────────────────────────────────────────────────────────────────

export type AccountType   = 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';
export type AccountNature = 'Debit' | 'Credit';
export type CurrencyControl       = 'ALL' | 'SINGLE' | 'BASE_ONLY';
export type CashFlowCategory      = 'OPERATING' | 'INVESTING' | 'FINANCING' | 'NONE';
export type FinancialStatementGroup = 'BALANCE_SHEET' | 'INCOME_STATEMENT';

// ── Account categories per type (mirrors backend seed data) ───────────────────

export const ACCOUNT_CATEGORIES: Record<AccountType, string[]> = {
  Asset:     ['Current Assets', 'Fixed Assets', 'Investments', 'Intangible Assets', 'Deferred Assets', 'Other Assets'],
  Liability: ['Current Liabilities', 'Long-term Liabilities', 'Deferred Liabilities', 'Contingent Liabilities', 'Other Liabilities'],
  Equity:    ['Paid-up Capital', 'Retained Earnings', 'Reserves', 'Other Equity'],
  Revenue:   ['Revenue', 'Other Income', 'Extraordinary Income'],
  Expense:   ['Cost of Goods Sold', 'Operating Expenses', 'Depreciation', 'Finance Costs', 'Tax Expense', 'Other Expenses'],
};

/**
 * Subcategory is routing, not description — these values are matched exactly, and
 * case-sensitively, by code that decides real behaviour:
 *
 *   Cash           which accounts appear in "Pay From" for cash methods, the Cash Book scope,
 *                  and the cash-due calculation
 *   ChequesInHand  where a received cheque lands, and what can be signed over to a supplier
 *
 * There is deliberately no "Bank": a bank account is a row in the bank account register, and the
 * Bank Book reads it from there. Offering a Bank tag here would be a second way to say the same
 * thing, which is the drift the Bank Book fix removed.
 *
 * Anything else is ignored. It was a free text box, so a typo routed nothing and said nothing.
 */
export const ACCOUNT_SUBCATEGORIES: { value: string; label: string }[] = [
  { value: 'Cash',          label: 'Cash — tills and cash on hand' },
  { value: 'ChequesInHand', label: 'Cheques in Hand — received, not yet banked' },
];

// ── DTOs ──────────────────────────────────────────────────────────────────────

export interface AccountDto {
  id:          string;
  companyId:   string;
  code:        string;
  name:        string;
  shortName?:  string | null;
  description?: string | null;

  accountType:       AccountType;
  accountNature:     AccountNature;
  accountCategory?:  string | null;
  accountSubcategory?: string | null;

  parentId?:        string | null;
  parentCode?:      string | null;
  parentName?:      string | null;
  isPostingAccount: boolean;
  isControlAccount: boolean;

  currencyControl?:        CurrencyControl | null;
  allowMultiCurrency:      boolean;
  taxApplicable:           boolean;
  cashFlowCategory?:       CashFlowCategory | null;
  financialStatementGroup?: FinancialStatementGroup | null;
  revaluationRequired:     boolean;
  isRetainedEarnings:      boolean;

  costCenterMandatory: boolean;
  locationMandatory:   boolean;
  projectMandatory:    boolean;
  departmentMandatory: boolean;

  allowManualJournal:    boolean;
  allowPaymentPosting:   boolean;
  allowReceiptPosting:   boolean;
  approvalRequired:      boolean;
  postingRestriction?:   string | null;
  transactionRestriction?: string | null;

  reportingGroup?:       string | null;
  reportingSequence?:    number | null;
  externalMappingCode?:  string | null;
  consolidationGroup?:   string | null;
  ifrsMappingCode?:      string | null;
  taxReportMappingCode?: string | null;
  dashboardGroup?:       string | null;
  /**
   * GAP-004 — default name to print on a cheque paid through this account, when it differs from
   * the account name. Null means fall back to the party being paid.
   */
  chequePayeeName?:      string | null;
  /**
   * ML-09 — how cheques settling through this account are crossed. Null leaves it to the bank
   * account's own default. A voucher may state something else, but that needs the override
   * permission.
   */
  defaultChequeCrossing?: string | null;

  isActive:          boolean;
  createdBy?:        string | null;
  createdByName?:    string | null;
  createdDate:       string;
  modifiedBy?:       string | null;
  modifiedByName?:   string | null;
  modifiedDate?:     string | null;
}

export interface AccountListDto {
  id:               string;
  companyId:        string;
  code:             string;
  name:             string;
  shortName?:       string | null;
  accountType:      AccountType;
  accountNature:    AccountNature;
  accountCategory?: string | null;
  parentId?:        string | null;
  parentCode?:      string | null;
  isPostingAccount: boolean;
  isControlAccount: boolean;
  isActive:         boolean;
  createdDate:      string;
}

export interface AccountTreeNodeDto {
  id:               string;
  code:             string;
  name:             string;
  shortName?:       string | null;
  accountType:      AccountType;
  accountNature:    AccountNature;
  accountCategory?: string | null;
  parentId?:        string | null;
  isPostingAccount: boolean;
  isControlAccount: boolean;
  isActive:         boolean;
  children:         AccountTreeNodeDto[];
}

// ── Filter ────────────────────────────────────────────────────────────────────

export interface AccountFilterDto {
  search?:           string;
  pageNumber?:       number;
  pageSize?:         number;
  isActive?:         boolean;
  isPostingAccount?: boolean;
  accountType?:      AccountType;
  companyId?:        string;
  /**
   * Read the chart through one location (GAP-003): accounts restricted to other locations drop
   * out. Omit it on maintenance screens, where the whole chart should be visible.
   */
  locationId?:       string;
}

// ── Location restriction (GAP-003) ────────────────────────────────────────────

export interface AccountLocationRef {
  locationId: string;
  code:       string;
  name:       string;
}

export interface AccountLocations {
  accountId:    string;
  /** False means the account may be used anywhere — the normal case. */
  isRestricted: boolean;
  locations:    AccountLocationRef[];
}

// ── Request payloads ──────────────────────────────────────────────────────────

export interface CreateAccountRequest {
  code:             string;
  name:             string;
  accountType:      AccountType;
  accountNature?:   AccountNature | null;
  isPostingAccount: boolean;
  parentId?:        string | null;
  description?:     string | null;
  shortName?:       string | null;
  accountCategory?:    string | null;
  accountSubcategory?: string | null;
  isControlAccount:    boolean;
  currencyControl?:        CurrencyControl | null;
  allowMultiCurrency:      boolean;
  taxApplicable:           boolean;
  cashFlowCategory?:       CashFlowCategory | null;
  financialStatementGroup?: FinancialStatementGroup | null;
  revaluationRequired:     boolean;
  isRetainedEarnings:      boolean;
  costCenterMandatory: boolean;
  locationMandatory:   boolean;
  projectMandatory:    boolean;
  departmentMandatory: boolean;
  allowManualJournal:    boolean;
  allowPaymentPosting:   boolean;
  allowReceiptPosting:   boolean;
  approvalRequired:      boolean;
  postingRestriction?:   string | null;
  transactionRestriction?: string | null;
  reportingGroup?:       string | null;
  reportingSequence?:    number | null;
  externalMappingCode?:  string | null;
  consolidationGroup?:   string | null;
  ifrsMappingCode?:      string | null;
  taxReportMappingCode?: string | null;
  dashboardGroup?:       string | null;
  /**
   * GAP-004 — default name to print on a cheque paid through this account, when it differs from
   * the account name. Null means fall back to the party being paid.
   */
  chequePayeeName?:      string | null;
  /**
   * ML-09 — how cheques settling through this account are crossed. Null leaves it to the bank
   * account's own default. A voucher may state something else, but that needs the override
   * permission.
   */
  defaultChequeCrossing?: string | null;
}

export interface UpdateAccountRequest extends CreateAccountRequest {
  isActive: boolean;
}

/**
 * Somewhere a voucher can take money from, or put it into (GAP-002/003/013).
 *
 * `kind` is the whole point: BANK carries a `bankAccountId` and posting resolves the GL account
 * through it, exactly as it always has. CASH names the GL account directly — the only route to a
 * till, which no voucher had before. CHEQUE is the same shape as CASH — a GL account chosen
 * directly, no bank account needed — offered only for a received cheque (GAP-013): it is not cash
 * yet, so it posts to a Cheques in Hand account instead of a till.
 */
export interface EligibleFundingSource {
  kind:              'BANK' | 'CASH' | 'PETTY_CASH' | 'CHEQUE';
  /** The GL account that actually gets posted, whichever kind this is. */
  accountId:         string;
  accountCode:       string;
  accountName:       string;
  /** Set for BANK only. Two bank accounts can share one GL account, so this is what tells them apart. */
  bankAccountId:     string | null;
  bankAccountNumber: string | null;
  currencyCode:      string;
  /**
   * Set for PETTY_CASH only. Two floats can share one Petty Cash GL account — a box per branch on
   * one control account — so the account cannot identify the box (GAP-012).
   */
  pettyCashFloatId?: string | null;
  /** What the float holds right now. Shown beside the choice; a box that cannot cover the payment
   *  is what the operator most needs to know before picking it. */
  availableBalance?: number | null;
}
